// Import CSV/TSV degli asset business critical per il wizard di onboarding (2026-10-19).
// Accetta il file esportato da un inventario o compilato a mano in Excel: separatore
// auto-rilevato (; , tab), intestazione riconosciuta per nome (in italiano o inglese); senza
// intestazione riconoscibile l'ordine atteso è hostname, IP, MAC, ruolo.

export interface CsvAssetRow {
  hostname: string;
  ipAddress: string;
  macAddress: string;
  roleDescription: string;
  nightAllowed: boolean;
}

const HEADERS: Record<keyof Omit<CsvAssetRow, 'nightAllowed'> | 'nightAllowed', string[]> = {
  hostname: ['hostname', 'host', 'name', 'nome', 'computer', 'device', 'dispositivo', 'nome host'],
  ipAddress: ['ip', 'ip_address', 'ip address', 'ipaddress', 'indirizzo ip', 'address'],
  macAddress: ['mac', 'mac_address', 'mac address', 'macaddress', 'indirizzo mac', 'physical address'],
  roleDescription: ['role', 'ruolo', 'description', 'descrizione', 'role_description', 'descrizione ruolo', 'function', 'funzione', 'notes', 'note'],
  nightAllowed: ['night', 'notte', 'night_allowed', 'acceso di notte', 'always on', 'sempre acceso', '24x7', '24/7'],
};

function detectDelimiter(line: string): string {
  const counts: Array<[string, number]> = [[';', 0], [',', 0], ['\t', 0]].map(([d]) => [d as string, line.split(d as string).length - 1]);
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ',';
}

function splitLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; }
      else quoted = !quoted;
    } else if (ch === delimiter && !quoted) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map(v => v.trim());
}

function truthy(v: string): boolean {
  return ['1', 'true', 'yes', 'si', 'sì', 'y', 'x', 'on'].includes(v.trim().toLowerCase());
}

export function parseCriticalAssetsCsv(text: string): { rows: CsvAssetRow[]; skipped: number; headerDetected: boolean } {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length === 0) return { rows: [], skipped: 0, headerDetected: false };
  const delimiter = detectDelimiter(lines[0]);
  const first = splitLine(lines[0], delimiter).map(h => h.toLowerCase());
  const index: Partial<Record<keyof CsvAssetRow, number>> = {};
  for (const [field, names] of Object.entries(HEADERS) as Array<[keyof CsvAssetRow, string[]]>) {
    const i = first.findIndex(h => names.includes(h));
    if (i >= 0) index[field] = i;
  }
  const headerDetected = index.hostname !== undefined || index.macAddress !== undefined || index.ipAddress !== undefined;
  if (!headerDetected) { index.hostname = 0; index.ipAddress = 1; index.macAddress = 2; index.roleDescription = 3; index.nightAllowed = 4; }
  const body = headerDetected ? lines.slice(1) : lines;
  const rows: CsvAssetRow[] = [];
  let skipped = 0;
  for (const line of body) {
    const cells = splitLine(line, delimiter);
    const get = (f: keyof CsvAssetRow) => (index[f] !== undefined ? cells[index[f]!] || '' : '');
    const row: CsvAssetRow = {
      hostname: get('hostname'), ipAddress: get('ipAddress'), macAddress: get('macAddress'),
      roleDescription: get('roleDescription'), nightAllowed: truthy(get('nightAllowed')),
    };
    if (!row.hostname && !row.ipAddress && !row.macAddress) { skipped++; continue; }
    rows.push(row);
  }
  return { rows, skipped, headerDetected };
}
